// src/utils/errorUtils.js
// ─────────────────────────────────────────────────────────────────────────────
// Translates raw technical errors into safe, user-friendly messages.
//
// Developers: full error always logged to console.
// Users:      generic message only — no stack traces, no internal details.
// Hackers:    nothing useful exposed.
// ─────────────────────────────────────────────────────────────────────────────

const isDev = process.env.NODE_ENV === 'development';

// Map of error signatures → friendly messages
const ERROR_MAP = [
  {
    match: (msg) => msg.includes('failed to fetch') || msg.includes('network'),
    friendly: 'Unable to connect to the server. Please check your internet connection and try again.',
  },
  {
    match: (msg) => msg.includes('401') || msg.includes('unauthorized') || msg.includes('could not validate') || msg.includes('not authenticated'),
    friendly: 'Your session has expired. Please log in again.',
  },
  {
    match: (msg) => msg.includes('403') || msg.includes('forbidden') || msg.includes("don't have access") || msg.includes('do not have access') || msg.includes('permission'),
    friendly: 'You don\'t have permission to perform this action.',
  },
  {
    match: (msg) => msg.includes('404') || msg.includes('not found'),
    friendly: 'The requested resource could not be found.',
  },
  {
    match: (msg) => msg.includes('429') || msg.includes('too many requests'),
    friendly: 'Too many requests. Please wait a moment and try again.',
  },
  {
    match: (msg) => msg.includes('500') || msg.includes('internal server'),
    friendly: 'A server error occurred. Please try again later.',
  },
  {
    match: (msg) => msg.includes('timeout') || msg.includes('timed out'),
    friendly: 'The request timed out. Please try again.',
  },
];

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

const UNSAFE_PATTERNS = [
  'stack', 'traceback', 'exception', 'syntaxerror',
  'typeerror', 'referenceerror', 'at line', 'sql',
  'database', 'connection refused', '127.0.0.1', 'localhost',
  'internal', '/usr/', '/home/', '/var/',
];
/**
 * getFriendlyError(error)
 *
 * Pass any Error object or string.
 * Returns a safe, user-friendly string.
 * Always logs the real error to console (dev + prod, for server log capture).
 *
 * Usage:
 *   } catch (err) {
 *     setError(getFriendlyError(err));
 *   }
 */
export function getFriendlyError(error, context = '') {
  const raw = error instanceof Error ? error.message : String(error ?? '');
  const lower = raw.toLowerCase();

  // Always log full error — visible in browser devtools and server log aggregators
  // but never exposed in the UI
  if (isDev) {
    console.group(`[Error]${context ? ` in ${context}` : ''}`);
    console.error(error);
    console.groupEnd();
  } else {
    // In production, still log so platforms like Sentry/Datadog/CloudWatch can capture it
    console.error(`[Error]${context ? ` in ${context}` : ''}:`, raw);
  }
   if (UNSAFE_PATTERNS.some(p => lower.includes(p))) {
    const match = ERROR_MAP.find(({ match }) => match(lower));
    return match ? match.friendly : FALLBACK_MESSAGE;
  }

  const match = ERROR_MAP.find(({ match }) => match(lower));
  return match ? match.friendly : (raw || FALLBACK_MESSAGE);
}